import styled from "@emotion/styled";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
} from "@mui/material";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useDeleteExtractionConfigurationMutation } from "Configurations/Api/Mutations/useDeleteExtractionConfigurationMutation";
import { useDuplicateExtractionConfigurationMutation } from "Configurations/Api/Mutations/useDuplicateExtractionConfigurationMutation";
import { useExtractionConfigurationsQuery } from "Configurations/Api/Queries/useExtractionConfigurationsQuery";
import { JsonViewer } from "Configurations/Components/JsonViewer";
import type { ExtractionConfigurationListItemDto } from "Infrastructure/Api/Api";
import { ConfirmDialog } from "Shared/Components/ConfirmDialog";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import type { FC } from "react";
import { useState } from "react";
import { toast } from "sonner";

const StyledCell = styled(Box)``;

const StyledTextCell = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StyledButtonCell = styled(Box)`
	display: flex;
	align-items: center;
`;

const StyledBooleanCell = styled(Box)`
	display: flex;
	align-items: center;
`;

const StyledActionsCell = styled(Box)`
	display: flex;
	align-items: center;
	gap: 8px;
	justify-content: flex-end;
`;

const StyledHeaderWithSort = styled(Box)`
	display: flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	user-select: none;
	&:hover {
		opacity: 0.7;
	}
`;

const StyledDialogContent = styled(DialogContent)`
	padding: 0;
	max-height: 70vh;
	overflow-y: auto;
`;

const StyledTextContent = styled.div`
	white-space: pre-wrap;
	word-break: break-word;
	font-family: monospace;
	font-size: 14px;
	padding: 24px;
`;

type DialogType = "text" | "json";

type SortColumn = "tenantCode" | "connectorProvider" | null;
type SortOrder = "asc" | "desc";

type Props = {
	onEdit: (config: ExtractionConfigurationListItemDto) => void;
	searchQuery?: string;
};

export const ExtractionConfigurationsTable: FC<Props> = ({
	onEdit,
	searchQuery = "",
}) => {
	const { data, isLoading } = useExtractionConfigurationsQuery(searchQuery);
	const { data: userInfo } = useUserInfoQuery();
	const deleteMutation = useDeleteExtractionConfigurationMutation();
	const duplicateMutation = useDuplicateExtractionConfigurationMutation();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogTitle, setDialogTitle] = useState("");
	const [dialogContent, setDialogContent] = useState("");
	const [dialogType, setDialogType] = useState<DialogType>("text");
	const [configToDelete, setConfigToDelete] =
		useState<ExtractionConfigurationListItemDto | null>(null);
	const [configToDuplicate, setConfigToDuplicate] =
		useState<ExtractionConfigurationListItemDto | null>(null);
	const [sortColumn, setSortColumn] = useState<SortColumn>(null);
	const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

	const rawConfigurations: ExtractionConfigurationListItemDto[] =
		data?.items ?? [];

	// Sort configurations (server-side filtering is already applied via searchQuery)
	// Bez zvoleneho razeni se seznam skada do stromu: potomci hned za svym rodicem,
	// jinak by u desitek producentu neslo poznat, co pod co patri.
	const asTree = (() => {
		const byParent = new Map<number | null, typeof rawConfigurations>();
		for (const c of rawConfigurations) {
			const key = c.parentConfigurationID ?? null;
			byParent.set(key, [...(byParent.get(key) ?? []), c]);
		}

		const out: Array<(typeof rawConfigurations)[number] & { depth: number }> = [];
		const walk = (parent: number | null, depth: number) => {
			for (const c of (byParent.get(parent) ?? []).sort((a, b) =>
				a.name.localeCompare(b.name),
			)) {
				out.push({ ...c, depth });
				walk(c.extractionConfigurationID, depth + 1);
			}
		};
		walk(null, 0);

		// Uzel, jehoz rodic v seznamu neni (jiny tenant, nenactena stranka), by jinak zmizel.
		for (const c of rawConfigurations) {
			if (!out.some((o) => o.extractionConfigurationID === c.extractionConfigurationID)) {
				out.push({ ...c, depth: 0 });
			}
		}
		return out;
	})();

	const configurations = [...asTree].sort((a, b) => {
		if (!sortColumn) return 0;

		const aValue = a[sortColumn] || "";
		const bValue = b[sortColumn] || "";

		const comparison = aValue.toString().localeCompare(bValue.toString());
		return sortOrder === "asc" ? comparison : -comparison;
	});

	// Check if user is admin (has Tenants access right)
	const isAdmin =
		userInfo?.status === 200 &&
		userInfo.data.accessRightCodes?.includes("Tenants");

	const handleSortClick = (column: SortColumn) => {
		if (sortColumn === column) {
			// Toggle sort order if clicking the same column
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			// Set new column and default to ascending
			setSortColumn(column);
			setSortOrder("asc");
		}
	};

	const getSortIcon = (column: SortColumn) => {
		if (sortColumn !== column) return null;
		return sortOrder === "asc" ? (
			<ArrowUpwardIcon fontSize="small" />
		) : (
			<ArrowDownwardIcon fontSize="small" />
		);
	};

	const handleOpenDialog = (
		title: string,
		content: string,
		type: DialogType = "text",
	) => {
		setDialogTitle(title);
		setDialogContent(content);
		setDialogType(type);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
	};

	const handleEdit = (config: ExtractionConfigurationListItemDto) => {
		onEdit(config);
	};

	const handleDeleteClick = (config: ExtractionConfigurationListItemDto) => {
		setConfigToDelete(config);
	};

	const handleConfirmDelete = async () => {
		if (configToDelete) {
			try {
				await deleteMutation.mutateAsync(
					configToDelete.extractionConfigurationID,
				);
				toast.success(
					`Configuration "${configToDelete.name}" deleted successfully`,
				);
				setConfigToDelete(null);
			} catch (error: any) {
				console.error("Delete failed:", error);

				// Check if it's a foreign key constraint error
				if (error?.status === 500 || error?.message?.includes("foreign key")) {
					toast.error(
						`Cannot delete "${configToDelete.name}" because it is being used by existing extractions. Please delete all associated extractions first.`,
						{ duration: 6000 },
					);
				} else {
					toast.error(
						`Failed to delete configuration: ${error?.message || "Unknown error"}`,
					);
				}
				setConfigToDelete(null);
			}
		}
	};

	const handleDuplicateClick = (config: ExtractionConfigurationListItemDto) => {
		setConfigToDuplicate(config);
	};

	const handleConfirmDuplicate = async () => {
		if (configToDuplicate) {
			try {
				await duplicateMutation.mutateAsync(
					configToDuplicate.extractionConfigurationID,
				);
				toast.success(
					`Configuration "${configToDuplicate.name}" duplicated successfully`,
				);
				setConfigToDuplicate(null);
			} catch (error: any) {
				console.error("Duplicate failed:", error);
				toast.error(
					`Failed to duplicate configuration: ${error?.message || "Unknown error"}`,
				);
				setConfigToDuplicate(null);
			}
		}
	};

	const handleCancelDelete = () => {
		setConfigToDelete(null);
	};

	return (
		<>
			<BlDataTable
				rows={configurations.map((x, index) => ({ data: x }))}
				rowEntityID="extractionConfigurationID"
				isLoading={isLoading}
				getHeaderCell={(column) => {
					const columnId = column.id as string;
					// Only make tenant and connector columns sortable
					if (columnId === "tenantCode" || columnId === "connectorProvider") {
						return (
							<StyledHeaderWithSort
								onClick={() =>
									handleSortClick(
										columnId as "tenantCode" | "connectorProvider",
									)
								}
							>
								<div>{column.label ?? column.id}</div>
								{getSortIcon(columnId as "tenantCode" | "connectorProvider")}
							</StyledHeaderWithSort>
						);
					}
					return <div>{column.label ?? column.id}</div>;
				}}
				columns={[
					{ id: "name" as const, label: "Name", fillRow: true },
					...(isAdmin ? [{ id: "tenantCode" as const, label: "Tenant" }] : []),
					{ id: "prompt" as const, label: "Prompt" },
					{ id: "jsonSchema" as const, label: "Schema" },
					{ id: "connectorProvider" as const, label: "Connector" },
					{ id: "pageLimit" as const, label: "Page Limit" },
					{ id: "forceOcr" as const, label: "Force OCR" },
					{ id: "isFallback" as const, label: "Fallback" },
					{ id: "isCatalogIdentification" as const, label: "Identification" },
					{ id: "identificationHints" as const, label: "Identification Hints" },
					{
						id: "action" as const,
						label: "Actions",
						maxWidth: 0,
						justifySelf: "flex-end",
					},
				]}
				rowsPerPage={10}
				getCell={(id, data) => {
					switch (id) {
						case "tenantCode":
							return <StyledCell>{data.tenantCode || "—"}</StyledCell>;
						case "name": {
							const depth = (data as typeof data & { depth?: number }).depth ?? 0;
							const title = data.parentName
								? `${data.parentName} › ${data.name}`
								: data.name;
							return (
								<Tooltip title={title} arrow>
									<StyledTextCell style={{ paddingLeft: depth * 18 }}>
										{depth > 0 && (
											<span style={{ opacity: 0.45, marginRight: 4 }}>└</span>
										)}
										{data.name}
									</StyledTextCell>
								</Tooltip>
							);
						}
						case "prompt":
							return (
								<StyledButtonCell>
									<Button
										variant="outlined"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleOpenDialog("Prompt", data.prompt, "text");
										}}
									>
										Show
									</Button>
								</StyledButtonCell>
							);
						case "jsonSchema":
							return (
								<StyledButtonCell>
									<Button
										variant="outlined"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleOpenDialog("Schema", data.jsonSchema, "json");
										}}
									>
										Show
									</Button>
								</StyledButtonCell>
							);
						case "connectorProvider":
							return <StyledCell>{data.connectorProvider || "—"}</StyledCell>;
						case "pageLimit":
							return <StyledCell>{data.pageLimit ?? "N/A"}</StyledCell>;
						case "forceOcr":
							return (
								<StyledBooleanCell>
									<Chip
										label={data.forceOcr ? "Yes" : "No"}
										color={data.forceOcr ? "success" : "default"}
										size="small"
									/>
								</StyledBooleanCell>
							);
						case "isFallback":
							return (
								<StyledBooleanCell>
									{data.isFallback ? (
										<Chip label="Fallback" color="info" size="small" />
									) : (
										"—"
									)}
								</StyledBooleanCell>
							);
						case "isCatalogIdentification":
							return (
								<StyledBooleanCell>
									{data.isCatalogIdentification ? (
										<Chip
											label="Identification"
											color="secondary"
											size="small"
										/>
									) : (
										"—"
									)}
								</StyledBooleanCell>
							);
						case "identificationHints":
							return data.identificationHints ? (
								<StyledButtonCell>
									<Button
										variant="outlined"
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleOpenDialog(
												"Identification Hints",
												data.identificationHints || "",
												"text",
											);
										}}
									>
										Show
									</Button>
								</StyledButtonCell>
							) : (
								<StyledCell>—</StyledCell>
							);
						case "action":
							return (
								<StyledActionsCell>
									<IconButton
										size="small"
										color="primary"
										onClick={(e) => {
											e.stopPropagation();
											handleEdit(data);
										}}
										title="Edit"
									>
										<EditIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										color="info"
										onClick={(e) => {
											e.stopPropagation();
											handleDuplicateClick(data);
										}}
										title="Duplicate"
									>
										<ContentCopyIcon fontSize="small" />
									</IconButton>
									<IconButton
										size="small"
										color="error"
										onClick={(e) => {
											e.stopPropagation();
											handleDeleteClick(data);
										}}
										title="Delete"
									>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</StyledActionsCell>
							);
						default:
							return <StyledCell />;
					}
				}}
			/>

			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>{dialogTitle}</DialogTitle>
				<StyledDialogContent>
					{dialogType === "json" ? (
						<JsonViewer jsonString={dialogContent} />
					) : (
						<StyledTextContent>{dialogContent}</StyledTextContent>
					)}
				</StyledDialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} color="primary">
						Close
					</Button>
				</DialogActions>
			</Dialog>

			{configToDelete && (
				<ConfirmDialog
					open={!!configToDelete}
					title="Delete Extraction Configuration"
					message={`Are you sure you want to delete the extraction configuration "${configToDelete.name}"? This action cannot be undone.\n\nNote: If this configuration is being used by existing extractions, the deletion will fail. You must delete all associated extractions first.`}
					confirmText="Delete"
					onConfirm={handleConfirmDelete}
					onCancel={handleCancelDelete}
					isLoading={deleteMutation.isPending}
				/>
			)}

			{configToDuplicate && (
				<ConfirmDialog
					open={!!configToDuplicate}
					title="Duplicate Extraction Configuration"
					message={`Are you sure you want to duplicate the extraction configuration "${configToDuplicate.name}"? A copy will be created with a numbered suffix.`}
					confirmText="Duplicate"
					onConfirm={handleConfirmDuplicate}
					onCancel={() => setConfigToDuplicate(null)}
					isLoading={duplicateMutation.isPending}
				/>
			)}
		</>
	);
};
